import type { NetworkAdapter } from '../adapters/network';
import { Analytics } from '../analytics';
import type { EvaluationPopupResult } from '../components/EvaluationPopup';
import { logger } from '../utils/core/logger';

interface EvaluationConfig {
    evaluationUrl: string;
    siteName: string;
    networkAdapter?: NetworkAdapter;
}

interface EvaluationData {
    rating:
        | 'Accurate & OK'
        | 'Accurate but Not OK'
        | 'Not Accurate but OK'
        | 'Not Accurate & Not OK';
    comment?: string;
    messageText: string;
    messageId: string;
    url: string;
    domain: string;
    pageTitle: string;
    timestamp: string;
    sessionId?: string;
    threadId?: string;
}

export interface EvaluationMessageContext {
    sessionId?: string;
    threadId?: string;
    userMessageContent?: string;
}

export interface EvaluationOverrideSubmission {
    result: EvaluationPopupResult;
    messageId: string;
    messageContent: string;
    sessionId: string;
    threadId?: string;
    userMessageContent?: string;
}

export type EvaluationSubmissionOverride = (
    submission: EvaluationOverrideSubmission
) => Promise<boolean>;

export class EvaluationService {
    private config: EvaluationConfig;
    private showPopupCallback?: (
        rating: 'thumbs_up' | 'thumbs_down'
    ) => Promise<EvaluationPopupResult>;

    constructor(config: EvaluationConfig) {
        this.config = config;
    }

    public setPopupCallback(
        callback: (rating: 'thumbs_up' | 'thumbs_down') => Promise<EvaluationPopupResult>
    ) {
        this.showPopupCallback = callback;
    }

    public async showEvaluationPopup(
        _messageText: string,
        messageId: string,
        rating: 'thumbs_up' | 'thumbs_down'
    ): Promise<EvaluationPopupResult> {
        // Track that popup was shown
        Analytics.trackEvaluationPopupShown({
            messageId,
            rating,
        });

        if (this.showPopupCallback) {
            const result = await this.showPopupCallback(rating);

            // Track if popup was cancelled
            if (result.cancelled) {
                Analytics.trackEvaluationPopupCancelled({
                    messageId,
                    rating,
                });
            }

            return result;
        }

        // Fallback if no callback is set
        logger.warn('EvaluationService: No popup callback set, using fallback');
        if (rating === 'thumbs_up') {
            return {
                rating: 'thumbs_up',
                finalRating: 'Accurate & OK',
                cancelled: false,
            };
        } else {
            return {
                rating: 'thumbs_down',
                finalRating: 'Accurate but Not OK',
                comment: 'No popup callback available',
                cancelled: false,
            };
        }
    }

    public async submitEvaluation(evaluationData: EvaluationData): Promise<boolean> {
        try {
            logger.debug(
                'EvaluationService: Submitting evaluation via analytics system:',
                evaluationData
            );

            // Use the new analytics system to submit evaluation
            await Analytics.trackEvaluationSubmitted({
                rating: evaluationData.rating,
                comment: evaluationData.comment,
                messageText: evaluationData.messageText,
                messageId: evaluationData.messageId,
                url: evaluationData.url,
                pageTitle: evaluationData.pageTitle,
                threadId: evaluationData.threadId,
            });

            logger.debug(
                'EvaluationService: Evaluation submitted successfully via analytics system'
            );
            return true;
        } catch (error) {
            logger.error(
                'EvaluationService: Error submitting evaluation via analytics system:',
                error,
                {
                    evaluationUrl: this.config.evaluationUrl,
                    messageId: evaluationData.messageId,
                }
            );

            // Fallback to direct submission if analytics fails (skip for analytics-only mode)
            if (!this.config.evaluationUrl.startsWith('analytics://')) {
                try {
                    const result = await this.callWebhookViaServiceWorker(evaluationData);
                    if (result?.success) {
                        logger.debug('EvaluationService: Evaluation submitted via fallback method');
                        return true;
                    }
                } catch (fallbackError) {
                    logger.error(
                        'EvaluationService: Fallback submission also failed:',
                        fallbackError,
                        {
                            evaluationUrl: this.config.evaluationUrl,
                            messageId: evaluationData.messageId,
                        }
                    );
                }
            } else {
                logger.debug(
                    'EvaluationService: Skipping webhook fallback for analytics-only mode'
                );
            }

            return false;
        }
    }

    private async callWebhookViaServiceWorker(
        data: EvaluationData
    ): Promise<{ success?: boolean }> {
        // Always use direct fetch like the main API calls do - bypass networkAdapter
        const response = await fetch(this.config.evaluationUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return response.json();
    }

    public saveEvaluationState(messageId: string): void {
        try {
            const domain = window.location.hostname;
            const evaluationsKey = `evaluations_${domain}`;

            const existingEvaluations = sessionStorage.getItem(evaluationsKey);
            const evaluations = existingEvaluations ? JSON.parse(existingEvaluations) : [];

            if (!evaluations.includes(messageId)) {
                evaluations.push(messageId);
                sessionStorage.setItem(evaluationsKey, JSON.stringify(evaluations));
            }
        } catch (error) {
            logger.error('EvaluationService: Error saving evaluation state:', error);
        }
    }

    public isMessageEvaluated(messageId: string): boolean {
        try {
            const domain = window.location.hostname;
            const evaluationsKey = `evaluations_${domain}`;
            const saved = sessionStorage.getItem(evaluationsKey);
            const evaluations = saved ? JSON.parse(saved) : [];
            return evaluations.includes(messageId);
        } catch (error) {
            logger.error('EvaluationService: Error checking evaluation state:', error, {
                messageId: messageId,
            });
            return false;
        }
    }

    public trackThumbsClick(messageId: string, rating: 'thumbs_up' | 'thumbs_down'): void {
        Analytics.trackEvaluationThumbsClicked({
            messageId,
            rating,
        });
    }

    updateConfig(newConfig: Partial<EvaluationConfig>) {
        this.config = { ...this.config, ...newConfig };
    }
}

export const createEvaluationService = (config: EvaluationConfig): EvaluationService => {
    return new EvaluationService(config);
};
